/**
 * useHomeARDemoInvoicePageDeskLogic
 *
 * Pure logic hook — state machine, orbital bridge, actions.
 * Use the companion component for rendering.
 *
 * @generated from schema
 * @trait HomeARDemoInvoicePageDesk
 */

import { useReducer, useCallback, useEffect, useRef, useMemo } from 'react';
import { emitCascadeEvent, useOrbitalBridge } from '@/hooks/useOrbitalBridge';
import type { EntityRow, EventPayload, EventPayloadValue, FieldValue } from '@almadar/core';
function getNestedValue(obj: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined, path: string): FieldValue | undefined {
  if (obj === null || obj === undefined || !path) return undefined;
  if (typeof obj !== 'object') return undefined;
  if (!path.includes('.') && !Array.isArray(obj)) {
    return (obj as Record<string, FieldValue | undefined>)[path];
  }
  let value: EventPayload | EventPayloadValue | FieldValue | Record<string, FieldValue | undefined> | null | undefined = obj;
  for (const part of path.split('.')) {
    if (value === null || value === undefined || typeof value !== 'object') return undefined;
    if (Array.isArray(value)) {
      if (!/^\d+$/.test(part)) return undefined;
      value = (value as readonly (EventPayloadValue | FieldValue | null | undefined)[])[Number(part)];
      continue;
    }
    value = (value as Record<string, FieldValue | undefined>)[part];
  }
  return value as FieldValue | undefined;
}
import type { EventResponse, HomeARDemoInvoicePageItem, HomeARDemoInvoicePageDeskEventPayloadMap } from '@app/shared';
import { useUIEvents, claimDelivery } from '@almadar/ui/hooks';
import { registerTraitSnapshot, type TraitStateSnapshot } from '@almadar/ui/lib';
import { createCommandSendPump } from '@almadar/ui/lib';
import type { BusEvent } from '@almadar/core';

// ============================================================================
// State Machine
// ============================================================================

export type HomeARDemoInvoicePageDeskState =
  | 'booting'
  | 'idle';

export type HomeARDemoInvoicePageDeskEvent =
  | 'INIT'
  | 'LIST_READY'
  | 'INVOICE_ONE_CHANGED'
  | 'INVOICE_FIVE_CHANGED'
  | 'ADD_LINE'
  | 'SEND_INVOICE'
  | 'PAYMENT_IN'
  | 'SEND_NEXT'
  | 'INVOICES_RESET'
  | 'INVOICES_LOADED'
  | 'OPEN_INVOICE'
  | 'TOUR_STEP'
  | 'TOUR_RESET'
  | 'BrowseItemLoaded';

type HomeARDemoInvoicePageDeskSlot = never;
interface HomeARDemoInvoicePageDeskInternalState {
  machineState: HomeARDemoInvoicePageDeskState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeARDemoInvoicePageDeskEvent | null;
  slotWriter: Partial<Record<HomeARDemoInvoicePageDeskSlot, HomeARDemoInvoicePageDeskEvent | null>>;
  loading: boolean;
  pendingEvent: HomeARDemoInvoicePageDeskEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeARDemoInvoicePageItem>;
}

type HomeARDemoInvoicePageDeskAction =
  | { type: 'EVENT_SUCCESS'; event: HomeARDemoInvoicePageDeskEvent; newState: string; data: Record<string, EntityRow[]>; payload?: EventPayload; fields?: Record<string, EventPayloadValue | undefined>; renderBearing?: boolean; renderWrite?: HomeARDemoInvoicePageDeskSlot[]; renderClear?: HomeARDemoInvoicePageDeskSlot[] }
  | { type: 'EVENT_ERROR'; error: string }
  | { type: 'RESET_ERROR' }
  | { type: 'START_EVENT'; event: HomeARDemoInvoicePageDeskEvent; from: string }
  | { type: 'END_EVENT' }
  | { type: 'SET_FIELD'; field: keyof HomeARDemoInvoicePageItem & string; value: EventPayloadValue | undefined }
  | { type: 'TICK'; updates: Record<string, EventPayloadValue | undefined>; collection: string };

function homeARDemoInvoicePageDeskReducer(
  state: HomeARDemoInvoicePageDeskInternalState,
  action: HomeARDemoInvoicePageDeskAction
): HomeARDemoInvoicePageDeskInternalState {
  switch (action.type) {
    case 'START_EVENT':
      return { ...state, loading: true, pendingEvent: action.event, pendingFrom: action.from };
    case 'END_EVENT':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null };
    case 'EVENT_SUCCESS':
      return {
        machineState: action.newState as HomeARDemoInvoicePageDeskState,
        data: { ...state.data, ...action.data },
        lastPayload: action.renderBearing ? (action.payload ?? state.lastPayload) : state.lastPayload,
        lastEvent: action.renderBearing ? action.event : state.lastEvent,
        slotWriter: {
          ...state.slotWriter,
          ...Object.fromEntries((action.renderWrite ?? []).map((s) => [s, action.event] as const)),
          ...Object.fromEntries((action.renderClear ?? []).map((s) => [s, null] as const)),
        },
        loading: false,
        pendingEvent: null,
        pendingFrom: null,
        error: null,
        fields: { ...state.fields, ...(action.fields ?? {}) } as Partial<HomeARDemoInvoicePageItem>,
      };
    case 'EVENT_ERROR':
      return { ...state, loading: false, pendingEvent: null, pendingFrom: null, error: action.error };
    case 'RESET_ERROR':
      return { ...state, error: null };
    case 'SET_FIELD': {
      // Back-compat for non-codegen consumers that still dispatch
      // SET_FIELD directly. Codegen folds field updates into
      // EVENT_SUCCESS so transitions commit in a single render.
      return {
        ...state,
        fields: { ...state.fields, [action.field]: action.value } as Partial<HomeARDemoInvoicePageItem>,
      };
    }
    case 'TICK': {
      // Batch-update entity fields for tick effects. A [runtime]-linked
      // trait's row lives in the `fields` mirror (transitions write it,
      // the view reads it), so updates ALWAYS merge there; the
      // `data` collection row is updated too when one exists.
      const tickFields = { ...state.fields, ...action.updates } as Partial<HomeARDemoInvoicePageItem>;
      const arr = state.data[action.collection];
      const target = arr?.[0];
      if (!target) return { ...state, fields: tickFields };
      const updated = { ...target, ...action.updates } as EntityRow;
      return {
        ...state,
        data: { ...state.data, [action.collection]: [updated, ...arr.slice(1)] },
        fields: tickFields,
      };
    }
    default:
      return state;
  }
}

const initialState: HomeARDemoInvoicePageDeskInternalState = {
  machineState: 'booting',
  data: {},
  lastPayload: undefined,
  lastEvent: null,
  slotWriter: {},
  loading: false,
  pendingEvent: null,
  pendingFrom: null,
  error: null,
  fields: {},
};

// ============================================================================
// Hook
// ============================================================================

export interface HomeARDemoInvoicePageDeskConfig {
  linkedEntity?: string;
  entityId?: string;
  routeParams?: Record<string, string | undefined>;
  initPayload?: EventPayload;
}

export interface HomeARDemoInvoicePageDeskLogicReturn {
  state: HomeARDemoInvoicePageDeskState;
  data: Record<string, EntityRow[]>;
  lastPayload: EventPayload | undefined;
  lastEvent: HomeARDemoInvoicePageDeskEvent | null;
  slotWriter: Partial<Record<HomeARDemoInvoicePageDeskSlot, HomeARDemoInvoicePageDeskEvent | null>>;
  loading: boolean;
  pendingEvent: HomeARDemoInvoicePageDeskEvent | null;
  pendingFrom: string | null;
  error: string | null;
  fields: Partial<HomeARDemoInvoicePageItem>;
  dispatch: (event: string, payload?: EventPayload) => Promise<EventResponse>;
  actions: { [K in keyof HomeARDemoInvoicePageDeskEventPayloadMap]: (payload?: HomeARDemoInvoicePageDeskEventPayloadMap[K]) => Promise<EventResponse> };
  config?: HomeARDemoInvoicePageDeskConfig;
}

const __CASCADE_EVENTS = new Set<string>(['ADD_LINE', 'BrowseItemLoaded', 'INVOICES_RESET', 'LIST_READY', 'PAYMENT_IN', 'SEND_INVOICE', 'SEND_NEXT', 'TOUR_RESET', 'TOUR_STEP']);

const __LEG_EVENTS: ReadonlySet<string> = new Set(['ADD_LINE', 'INIT', 'INVOICES_RESET', 'INVOICE_FIVE_CHANGED', 'INVOICE_ONE_CHANGED', 'PAYMENT_IN', 'SEND_INVOICE', 'SEND_NEXT']);

export function useHomeARDemoInvoicePageDeskLogic(config?: Partial<HomeARDemoInvoicePageDeskConfig>): HomeARDemoInvoicePageDeskLogicReturn {
  const mergedConfig = config;

  // Orbital bridge: event bus + server communication
  const { sendEvent, eventBus } = useOrbitalBridge({ basePath: '/api/home-ar-demo-invoice-page-desk', orbitalName: 'HomeAR', traitName: 'HomeARDemoInvoicePageDesk' });

  // Internal state with reducer
  const [internalState, dispatchReducer] = useReducer(homeARDemoInvoicePageDeskReducer, initialState);
  const { machineState: state, data, lastPayload, lastEvent, slotWriter, loading, pendingEvent, pendingFrom, error, fields: _initialFields } = internalState;
  const fields = _initialFields;
  const internalStateRef = useRef(internalState);
  internalStateRef.current = internalState;

  const cascadeReceivedRef = useRef<TraitStateSnapshot['cascadeReceived']>([]);

  const machineStateRef = useRef(state);
  machineStateRef.current = state;

  const fieldsRef = useRef<EntityRow>({ ...fields });
  fieldsRef.current = { ...fields };
  const commandPump = useMemo(() => createCommandSendPump(), []);

  // Actor model: sequential event queue (prevents cross-trait race conditions)
  const eventQueueRef = useRef<Array<{ event: string; payload?: EventPayload; settle?: () => void }>>([]);
  const processingRef = useRef(false);

  // Transpiled client-side effects (Phase 4)
  // Each (fromState, event) transition's client effects are compiled to TypeScript.
  const executeTransitionEffects = useCallback((fromState: string, event: string, liveData: Record<string, EntityRow[]>, _payload?: EventPayload): { fields: Record<string, EventPayloadValue | undefined>; newState: string | undefined; emits: Array<() => void> } => {
    const _fields: Record<string, EventPayloadValue | undefined> = {};
    const _emits: Array<() => void> = [];
    let _newState: string | undefined = undefined;
    const entity: EntityRow[] = liveData['HomeARDemoInvoicePageItem'] ?? [];
    let fields: Record<string, EventPayloadValue | undefined> = entity[0] ?? {};

    if (fromState === 'booting' && event === 'INIT') {
      // booting → booting (on INIT)
      _newState = 'booting';
    } else if (fromState === 'booting' && event === 'LIST_READY') {
      // booting → idle (on LIST_READY)
        _emits.push(((_p) => () => { eventBus.emit('OPEN_INVOICE', _p, { trait: 'HomeARDemoInvoicePageDesk' }); emitCascadeEvent(eventBus, 'HomeAR', 'HomeARDemoInvoicePageDesk', { event: 'OPEN_INVOICE', payload: _p, source: { orbital: 'HomeAR', trait: 'HomeARDemoInvoicePageDesk' } }); })({ id: "invoice-1" as const }));
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'INIT') {
      // idle → idle (on INIT)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'INVOICE_ONE_CHANGED') {
      // idle → idle (on INVOICE_ONE_CHANGED)
        eventBus.emit('OPEN_INVOICE', { id: "invoice-1" as const }, { trait: 'HomeARDemoInvoicePageDesk' });
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'INVOICE_FIVE_CHANGED') {
      // idle → idle (on INVOICE_FIVE_CHANGED)
        eventBus.emit('OPEN_INVOICE', { id: "invoice-5" as const }, { trait: 'HomeARDemoInvoicePageDesk' });
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'ADD_LINE') {
      // idle → idle (on ADD_LINE)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'SEND_INVOICE') {
      // idle → idle (on SEND_INVOICE)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'PAYMENT_IN') {
      // idle → idle (on PAYMENT_IN)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'SEND_NEXT') {
      // idle → idle (on SEND_NEXT)
      _newState = 'idle';
    } else if (fromState === 'idle' && event === 'INVOICES_RESET') {
      // idle → idle (on INVOICES_RESET)
      _newState = 'idle';
    }
    return { fields: _fields, newState: _newState, emits: _emits };
  }, [eventBus]);

  // Per-transition render-ui presence map (codegen).
  // Used by dispatch to skip lastPayload/lastEvent updates for
  // transitions whose only effects are server-side or set/emit —
  // those don't change the rendered JSX, so the view should keep
  // the prior frame's payload until a render-bearing transition
  // (e.g. fetch.emit.success cascade) lands.
  const getRenderUiSlots = useCallback((fromState: string, event: string): { write: HomeARDemoInvoicePageDeskSlot[]; clear: HomeARDemoInvoicePageDeskSlot[] } => {
    return { write: [], clear: [] };
  }, []);

  /**
   * Dispatch an event through the orbital bridge.
   * Bridge emits lifecycle events on the bus, forwards to server.
   * Client effects are transpiled at compile time (Phase 4).
   */
  const dispatch = useCallback(async (event: string, __rawPayload?: EventPayload): Promise<EventResponse> => {
    const payload: EventPayload | undefined =
      __rawPayload !== undefined && ('entityId' in __rawPayload || 'id' in __rawPayload)
        ? __rawPayload
        : config?.entityId !== undefined
          ? { ...(__rawPayload ?? {}), entityId: config.entityId }
          : __rawPayload;
    const prevState = machineStateRef.current;
    // T9 optimistic dispatch: the fields mirror IS the [runtime] entity
    // row, so guard + to-state resolve locally on the same data the
    // server would read. Commit first, POST after; reconcile below.
    const liveData: Record<string, EntityRow[]> = { 'HomeARDemoInvoicePageItem': [fieldsRef.current as EntityRow] };
    const _optimistic = executeTransitionEffects(prevState, event, liveData, payload);
    if (_optimistic.newState === undefined) {
      // Guard rejected or no transition defined — mirrors the server's
      // rejection, without the round trip.
      const errorMsg = `No transition defined for ${event} from ${prevState} (or guard rejected)`;
      dispatchReducer({ type: 'EVENT_ERROR', error: errorMsg });
      return { success: false, transitioned: false, states: {}, emittedEvents: [], error: errorMsg } as EventResponse;
    }
    const _renderSlots = getRenderUiSlots(prevState, event);
    const _renderBearing = _renderSlots.write.length > 0 || _renderSlots.clear.length > 0;
    // Snapshot the touched fields' prior values for a reconcile rollback.
    const _snapshot: Record<string, EventPayloadValue | undefined> = {};
    for (const _k of Object.keys(_optimistic.fields)) { _snapshot[_k] = fieldsRef.current[_k]; }
    dispatchReducer({
      type: 'EVENT_SUCCESS',
      event: event as HomeARDemoInvoicePageDeskEvent,
      newState: _optimistic.newState,
      data: {},
      payload,
      fields: _optimistic.fields,
      renderBearing: _renderBearing,
      renderWrite: _renderSlots.write,
      renderClear: _renderSlots.clear,
    });
    if (__CASCADE_EVENTS.has(event)) {
      cascadeReceivedRef.current.push({ event, ...(payload !== undefined ? { payload } : {}), timestamp: Date.now() });
    }
    machineStateRef.current = _optimistic.newState as typeof machineStateRef.current;
    fieldsRef.current = { ...fieldsRef.current, ..._optimistic.fields } as EntityRow;
    for (const _emit of _optimistic.emits) _emit();

    if (__LEG_EVENTS.has(event)) {
    // Server leg: ordered by the pump, never blocking the drain.
    commandPump.enqueue(async () => {
      try {
        const response = await sendEvent(event, payload, prevState);
        if (response.success) {
          // Agreement — merge any fetch/persist results the local commit
          // couldn't supply (same reducer action, deferred leg).
          if (response.data && Object.keys(response.data).length > 0) {
            dispatchReducer({
              type: 'EVENT_SUCCESS',
              event: event as HomeARDemoInvoicePageDeskEvent,
              newState: machineStateRef.current,
              data: response.data,
              renderBearing: false,
            });
          }
        } else {
          // Rejection or state disagreement — roll back to the
          // pre-dispatch snapshot; the server stays authoritative.
          dispatchReducer({
            type: 'EVENT_SUCCESS',
            event: event as HomeARDemoInvoicePageDeskEvent,
            newState: prevState,
            data: {},
            fields: _snapshot,
            renderBearing: false,
          });
          machineStateRef.current = prevState;
          fieldsRef.current = { ...fieldsRef.current, ..._snapshot } as EntityRow;
          dispatchReducer({ type: 'EVENT_ERROR', error: response.error ?? `server state mismatch: expected ${_optimistic.newState}, got ${response.states?.['HomeARDemoInvoicePageDesk'] ?? '(unchanged)'}` });
        }
      } catch (err) {
        // Pump isolates the rejection; surface it without rolling
        // back — local state may simply be ahead of a reconnecting server.
        dispatchReducer({ type: 'EVENT_ERROR', error: err instanceof Error ? err.message : 'Network error' });
      }
    });
    }
    return { success: true, transitioned: true, states: { 'HomeARDemoInvoicePageDesk': _optimistic.newState }, emittedEvents: [] } as EventResponse;
  }, [sendEvent, executeTransitionEffects, commandPump, config?.entityId]);

  // Actor model: drain event queue one at a time
  const drainQueue = useCallback(async () => {
    if (processingRef.current) return;
    processingRef.current = true;
    try {
      while (eventQueueRef.current.length > 0) {
        const entry = eventQueueRef.current.shift()!;
        try { await dispatch(entry.event, entry.payload); } finally { entry.settle?.(); }
      }
    } finally {
      processingRef.current = false;
    }
  }, [dispatch]);

  // Actor model: enqueue event and start drain
  const enqueueEvent = useCallback((event: string, payload?: EventPayload) => {
    return new Promise<void>((settle) => {
    eventQueueRef.current.push({ event, payload, settle });
    void drainQueue();
    });
  }, [drainQueue]);

  // Action creators (typed per-event)
  const actions: { [K in keyof HomeARDemoInvoicePageDeskEventPayloadMap]: (payload?: HomeARDemoInvoicePageDeskEventPayloadMap[K]) => Promise<EventResponse> } = {
    'INIT': () => dispatch('INIT'),
    'LIST_READY': () => dispatch('LIST_READY'),
    'INVOICE_ONE_CHANGED': () => dispatch('INVOICE_ONE_CHANGED'),
    'INVOICE_FIVE_CHANGED': () => dispatch('INVOICE_FIVE_CHANGED'),
    'ADD_LINE': () => dispatch('ADD_LINE'),
    'SEND_INVOICE': () => dispatch('SEND_INVOICE'),
    'PAYMENT_IN': () => dispatch('PAYMENT_IN'),
    'SEND_NEXT': () => dispatch('SEND_NEXT'),
    'INVOICES_RESET': () => dispatch('INVOICES_RESET'),
    'INVOICES_LOADED': (payload?: HomeARDemoInvoicePageDeskEventPayloadMap['INVOICES_LOADED']) => dispatch('INVOICES_LOADED', payload),
    'OPEN_INVOICE': (payload?: HomeARDemoInvoicePageDeskEventPayloadMap['OPEN_INVOICE']) => dispatch('OPEN_INVOICE', payload),
  };

  useUIEvents(enqueueEvent, 'HomeAR.HomeARDemoInvoicePageDesk', ['INIT', 'LIST_READY', 'INVOICE_ONE_CHANGED', 'INVOICE_FIVE_CHANGED', 'ADD_LINE', 'SEND_INVOICE', 'PAYMENT_IN', 'SEND_NEXT', 'INVOICES_RESET'] as const, eventBus);

  // Verifier snapshot registration (VG Foundation 1)
  useEffect(() => {
    const unregister = registerTraitSnapshot('HomeARDemoInvoicePageDesk', (): TraitStateSnapshot => ({
      traitName: 'HomeARDemoInvoicePageDesk',
      currentState: internalStateRef.current.machineState,
      states: ['booting', 'idle'],
      events: ['INIT', 'LIST_READY', 'INVOICE_ONE_CHANGED', 'INVOICE_FIVE_CHANGED', 'ADD_LINE', 'SEND_INVOICE', 'PAYMENT_IN', 'SEND_NEXT', 'INVOICES_RESET', 'INVOICES_LOADED', 'OPEN_INVOICE'],
      data: internalStateRef.current.data,
      lastPayload: internalStateRef.current.lastPayload,
      lastEventDispatched: internalStateRef.current.lastEvent !== null
        ? {
            event: internalStateRef.current.lastEvent,
            ...(internalStateRef.current.lastPayload !== undefined
              ? { payload: internalStateRef.current.lastPayload }
              : {}),
            timestamp: Date.now(),
          }
        : undefined,
      cascadeReceived: cascadeReceivedRef.current,
    }));
    return unregister;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-init on mount
  useEffect(() => {
    const __rp = Object.fromEntries(Object.entries(config?.routeParams ?? {}).filter(([, v]) => v !== undefined));
    const __init = { ...__rp, ...(config?.initPayload ?? {}), ...(config?.entityId !== undefined ? { entityId: config.entityId } : {}) };
    dispatch('INIT', Object.keys(__init).length > 0 ? __init : undefined);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cross-trait event listeners (gap #13: qualified bus keys)
  useEffect(() => {
    // Listen for 'TOUR_STEP' (from HomeARDemoInvoicePageTour) → trigger 'ADD_LINE'
    const unsub_tour_step_from_homeardemoinvoicepagetour_to_add_line = eventBus.on('UI:HomeAR.HomeARDemoInvoicePageTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "ADD_LINE"))) return;
      if (!claimDelivery(event.source, 'HomeAR.HomeARDemoInvoicePageDesk', 'ADD_LINE')) return;
      enqueueEvent('ADD_LINE', event.payload);
    });
    // Listen for 'TOUR_STEP' (from HomeARDemoInvoicePageTour) → trigger 'SEND_INVOICE'
    const unsub_tour_step_from_homeardemoinvoicepagetour_to_send_invoice = eventBus.on('UI:HomeAR.HomeARDemoInvoicePageTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "SEND_INVOICE"))) return;
      if (!claimDelivery(event.source, 'HomeAR.HomeARDemoInvoicePageDesk', 'SEND_INVOICE')) return;
      enqueueEvent('SEND_INVOICE', event.payload);
    });
    // Listen for 'TOUR_STEP' (from HomeARDemoInvoicePageTour) → trigger 'PAYMENT_IN'
    const unsub_tour_step_from_homeardemoinvoicepagetour_to_payment_in = eventBus.on('UI:HomeAR.HomeARDemoInvoicePageTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "PAYMENT_IN"))) return;
      if (!claimDelivery(event.source, 'HomeAR.HomeARDemoInvoicePageDesk', 'PAYMENT_IN')) return;
      enqueueEvent('PAYMENT_IN', event.payload);
    });
    // Listen for 'TOUR_STEP' (from HomeARDemoInvoicePageTour) → trigger 'SEND_NEXT'
    const unsub_tour_step_from_homeardemoinvoicepagetour_to_send_next = eventBus.on('UI:HomeAR.HomeARDemoInvoicePageTour.TOUR_STEP', (event: BusEvent) => {
      if (!(((event.payload as EventPayload | undefined)?.event === "SEND_NEXT"))) return;
      if (!claimDelivery(event.source, 'HomeAR.HomeARDemoInvoicePageDesk', 'SEND_NEXT')) return;
      enqueueEvent('SEND_NEXT', event.payload);
    });
    // Listen for 'TOUR_RESET' (from HomeARDemoInvoicePageTour) → trigger 'INVOICES_RESET'
    const unsub_tour_reset_from_homeardemoinvoicepagetour_to_invoices_reset = eventBus.on('UI:HomeAR.HomeARDemoInvoicePageTour.TOUR_RESET', (event: BusEvent) => {
      if (!claimDelivery(event.source, 'HomeAR.HomeARDemoInvoicePageDesk', 'INVOICES_RESET')) return;
      enqueueEvent('INVOICES_RESET', event.payload);
    });
    // Listen for 'BrowseItemLoaded' (from HomeARDemoInvoicePageList) → trigger 'LIST_READY'
    const unsub_browseitemloaded_from_homeardemoinvoicepagelist_to_list_ready = eventBus.on('UI:HomeAR.HomeARDemoInvoicePageList.BrowseItemLoaded', (event: BusEvent) => {
      if (!event.source || !(event.source as { fromBridge?: boolean }).fromBridge) {
        return;
      }
      if (!claimDelivery(event.source, 'HomeAR.HomeARDemoInvoicePageDesk', 'LIST_READY')) return;
      enqueueEvent('LIST_READY', event.payload);
    });
    return () => {
      if (typeof unsub_tour_step_from_homeardemoinvoicepagetour_to_add_line === 'function') unsub_tour_step_from_homeardemoinvoicepagetour_to_add_line();
      if (typeof unsub_tour_step_from_homeardemoinvoicepagetour_to_send_invoice === 'function') unsub_tour_step_from_homeardemoinvoicepagetour_to_send_invoice();
      if (typeof unsub_tour_step_from_homeardemoinvoicepagetour_to_payment_in === 'function') unsub_tour_step_from_homeardemoinvoicepagetour_to_payment_in();
      if (typeof unsub_tour_step_from_homeardemoinvoicepagetour_to_send_next === 'function') unsub_tour_step_from_homeardemoinvoicepagetour_to_send_next();
      if (typeof unsub_tour_reset_from_homeardemoinvoicepagetour_to_invoices_reset === 'function') unsub_tour_reset_from_homeardemoinvoicepagetour_to_invoices_reset();
      if (typeof unsub_browseitemloaded_from_homeardemoinvoicepagelist_to_list_ready === 'function') unsub_browseitemloaded_from_homeardemoinvoicepagelist_to_list_ready();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventBus, dispatch]);

  return {
    state,
    data,
    lastPayload,
    lastEvent,
    slotWriter,
    loading,
    pendingEvent,
    pendingFrom,
    error,
    fields,
    dispatch,
    actions,
    config: mergedConfig,
  };
}
